import { FC } from 'react';

import { useFormik } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { StoreProductForm, validationSchema } from '../form';

import { StoreProductRewardPicker } from './storeProductRewardPicker/StoreProductRewardPicker';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { RichTextarea } from 'v2/components/richTextarea/RichTextarea';
import { StoreProductType } from 'v2/constants';

interface StoreProductFormDialog {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: StoreProductForm;
  isSubmitting: boolean;
  onSubmit: (values: StoreProductForm, resetForm: VoidFunction) => void;
}

export const StoreProductFormDialog: FC<StoreProductFormDialog> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const formik = useFormik<StoreProductForm>({
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
    enableReinitialize: true,
  });

  const handleTypeChange = (type: StoreProductType) => {
    formik.setFieldValue('type', type);
    formik.setFieldValue('value', '');
  };

  const handleValueChange = (value: string) => {
    formik.setFieldValue('value', value);
  };

  const handleSelectImage = (fileId: string) => {
    formik.setFieldValue('iconId', fileId);
  };

  const handlePromoCodesChange = (value: string[]) => {
    formik.setFieldValue('promocodes', value);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="storeProductForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
          <TextField
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
            required
          />

          <TextField
            name="shortDescription"
            value={formik.values.shortDescription}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Short Description"
            multiline
            rows={3}
            required
          />

          <RichTextarea
            value={formik.values.fullDescription}
            onChange={(v) => formik.setFieldValue('fullDescription', v)}
            label="Full Description"
          />

          <StoreProductRewardPicker
            type={formik.values.type}
            onTypeChange={handleTypeChange}
            value={formik.values.value ?? ''}
            onValueChange={handleValueChange}
            casinoLink={formik.values.casinoLink}
            casinoName={formik.values.casinoName}
            promoCodes={formik.values.promocodes}
            onFormikChange={formik.handleChange}
            onPromoCodesChange={handlePromoCodesChange}
          />

          <div className="form-group">
            <TextField
              name="price"
              value={formik.values.price}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Price"
              type="number"
              required
            />

            <TextField
              name="lockDays"
              value={formik.values.lockDays}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Lock Days"
              type="number"
              required
            />

            <TextField
              name="count"
              value={formik.values.count}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Count"
              type="number"
              required
            />
          </div>

          <FileSelector
            value={formik.values.iconId}
            onSelect={handleSelectImage}
            error={formik.errors.iconId}
            label="Preview"
            required
          />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="storeProductForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
